import React, { useState, useEffect } from 'react';
import { 
  X, 
  Key, 
  Globe, 
  Save, 
  RotateCw, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Eye, 
  EyeOff,
  Radio
} from 'lucide-react';
import { GatewayConfig } from '../types';
import { testGatewayConnection, DEFAULT_CONFIG } from '../services/api';

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: GatewayConfig;
  onSave: (newConfig: GatewayConfig) => void;
}

export const ConfigModal: React.FC<ConfigModalProps> = ({
  isOpen,
  onClose,
  config,
  onSave
}) => {
  const [baseUrl, setBaseUrl] = useState(config.baseUrl);
  const [bearerToken, setBearerToken] = useState(config.bearerToken);
  const [isDemoMode, setIsDemoMode] = useState(config.isDemoMode);
  const [showToken, setShowToken] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    latencyMs: number;
    message: string;
    statusCode?: number;
  } | null>(null);

  useEffect(() => {
    setBaseUrl(config.baseUrl);
    setBearerToken(config.bearerToken);
    setIsDemoMode(config.isDemoMode);
    setTestResult(null);
  }, [config, isOpen]);

  // Esc key listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await testGatewayConnection(baseUrl, bearerToken);
    setTestResult(res);
    setTesting(false);
  };

  const handleSave = () => {
    onSave({
      baseUrl: baseUrl.trim() || DEFAULT_CONFIG.baseUrl,
      bearerToken: bearerToken.trim(),
      isDemoMode: isDemoMode
    });
    onClose();
  };

  const handleReset = () => {
    setBaseUrl(DEFAULT_CONFIG.baseUrl);
    setBearerToken('');
    setIsDemoMode(true);
    setTestResult(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      
      {/* Click outside backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal Dialog */}
      <div 
        role="dialog"
        aria-label="Gateway Configuration"
        className="relative w-full max-w-lg bg-[#0d131f] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden z-10 text-xs font-sans animate-in zoom-in-95 duration-200"
      >
        
        {/* Header */}
        <div className="p-5 border-b border-slate-800/80 bg-slate-900/80 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
              <Globe className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">
                Gateway & Telemetry Configuration
              </h2>
              <p className="text-slate-400 text-[11px] font-mono">
                Persisted in browser localStorage
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close configuration"
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form Body */}
        <div className="p-5 space-y-4 font-mono">
          
          {/* Base URL */}
          <div className="space-y-1.5">
            <label className="text-slate-300 font-semibold text-xs block">
              Gateway Base URL
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://9router.hanavy.online"
              className="w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none focus:border-cyan-500 transition-colors"
            />
            <p className="text-[10px] text-slate-400 font-sans">
              Default proxy target: <code className="text-cyan-400">https://9router.hanavy.online</code>
            </p>
          </div>

          {/* Bearer Token */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-slate-300 font-semibold text-xs flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5 text-cyan-400" />
                <span>Bearer Token</span>
              </label>
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="text-[10px] text-slate-400 hover:text-slate-300 flex items-center gap-1 font-sans cursor-pointer"
              >
                {showToken ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                <span>{showToken ? 'Hide' : 'Reveal'}</span>
              </button>
            </div>
            <input
              type={showToken ? 'text' : 'password'}
              value={bearerToken}
              onChange={(e) => setBearerToken(e.target.value)}
              placeholder="Enter Bearer auth token..."
              className="w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 text-xs focus:outline-none focus:border-cyan-500 transition-colors"
            />
            <p className="text-[10px] text-slate-400 font-sans">
              Used for Authorization: Bearer &lt;token&gt; requests when polling remote telemetry.
            </p>
          </div>

          {/* Mode Switch: Demo Mode vs Live Gateway */}
          <div className="p-3.5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-slate-200 font-semibold text-xs font-sans">
                  Realtime Simulation Fallback
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDemoMode}
                  onChange={(e) => setIsDemoMode(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-cyan-600"></div>
              </label>
            </div>
            <p className="text-[11px] text-slate-400 font-sans leading-relaxed">
              When enabled, generates a dynamic stream of Mac (Apple Silicon) and VPS tool calls. 
              Turn off to poll exclusively from the configured live gateway endpoint.
            </p>
          </div>

          {/* Test Connection Button & Result */}
          <div className="pt-1">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-mono text-xs transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RotateCw className={`w-3.5 h-3.5 text-cyan-400 ${testing ? 'animate-spin' : ''}`} />
              <span>{testing ? 'Probing Gateway Endpoint...' : 'Test Gateway Connection'}</span>
            </button>

            {testResult && (
              <div className={`mt-2.5 p-3 rounded-lg border text-xs font-mono flex items-start gap-2 ${
                testResult.success 
                  ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-300' 
                  : 'bg-rose-950/40 border-rose-800/60 text-rose-300'
              }`}>
                {testResult.success ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                )}
                <div>
                  <div className="font-semibold">{testResult.message}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 font-sans">
                    Round-trip latency: {testResult.latencyMs}ms
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800/80 bg-slate-900/80 flex items-center justify-between">
          <button
            type="button"
            onClick={handleReset}
            className="text-slate-400 hover:text-slate-200 text-xs font-mono transition-colors"
          >
            Reset Defaults
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold shadow-lg shadow-cyan-600/20 transition-colors cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" />
              <span>Save & Apply</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
